import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Badge } from './ui/badge';
import PaginationBar from './PaginationBar';
import { Textarea } from './ui/textarea';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import SortDropdown from './SortDropdown';
import MultiSelectFilter from './MultiSelectFilter';
import api from '../lib/api';
import { h, writePrintHtml } from '../lib/printHelpers';
import { sortVariants } from '../lib/sortVariants';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkActionBar from './BulkActionBar';

const PurchaseEntryManagement = ({ user }) => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const bulk = useBulkSelect();
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [enums, setEnums] = useState({ sizes: [], colors: [] });
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [viewingEntry, setViewingEntry] = useState(null);
  const [expandedEntry, setExpandedEntry] = useState(null);

  // Filters
  const [searchGRN, setSearchGRN] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  const [filterStates, setFilterStates] = useState([]);
  const [filterCities, setFilterCities] = useState([]);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterStaff, setFilterStaff] = useState([]);
  const [sortBy, setSortBy] = useState('date_desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 200;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // Form state
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [challanNumber, setChallanNumber] = useState('');
  const [entryNotes, setEntryNotes] = useState('');
  const [entryItems, setEntryItems] = useState([]);
  const [vendorPOs, setVendorPOs] = useState([]);
  const [duplicateInvoiceWarning, setDuplicateInvoiceWarning] = useState(null);

  // Add item state
  const [addMode, setAddMode] = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);
  const [selectedPOItem, setSelectedPOItem] = useState(null);
  const [manualDesignCode, setManualDesignCode] = useState('');

  const checkDuplicateInvoice = async (invoiceNum, vendorId) => {
    if (!invoiceNum?.trim() || !vendorId) { setDuplicateInvoiceWarning(null); return; }
    try {
      const res = await api.get(`/purchase-entries/check-duplicate-invoice?vendor_id=${vendorId}&invoice_number=${encodeURIComponent(invoiceNum.trim())}${editingEntry ? `&exclude_id=${editingEntry.id}` : ''}`);
      if (res.data.duplicate) {
        setDuplicateInvoiceWarning(`Duplicate! Invoice "${invoiceNum}" already exists for this vendor (${res.data.grn_number})`);
      } else {
        setDuplicateInvoiceWarning(null);
      }
    } catch { setDuplicateInvoiceWarning(null); }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canCreate = ['super_admin', 'admin', 'manager', 'inventory_staff'].includes(user?.role);
  const canEdit = ['super_admin', 'admin', 'manager'].includes(user?.role);

  useKeyboardShortcuts({
    onSave: () => { if (isFormOpen && entryItems.length > 0) submitEntry(); },
    onEscape: () => { if (viewingEntry) setViewingEntry(null); },
    onNew: () => { if (!isFormOpen && canCreate) openCreateForm(); },
  });

  const loadData = async () => {
    try {
      const [entriesRes, vendorsRes, productsRes, brandsRes, enumsRes, warehousesRes] = await Promise.allSettled([
        api.get('/purchase-entries'),
        api.get('/vendors'),
        api.get('/products'),
        api.get('/brands'),
        api.get('/enums'),
        api.get('/warehouses'),
      ]);
      if (entriesRes.status === 'fulfilled') {
        setEntries(entriesRes.value.data);
        setTotalCount(parseInt(entriesRes.value.headers?.['x-total-count'] || entriesRes.value.data.length));
        setCurrentPage(1);
      }
      if (vendorsRes.status === 'fulfilled') setVendors(vendorsRes.value.data);
      if (productsRes.status === 'fulfilled') setProducts(productsRes.value.data);
      if (brandsRes.status === 'fulfilled') setBrands(brandsRes.value.data);
      if (enumsRes.status === 'fulfilled') setEnums(enumsRes.value.data);
      if (warehousesRes.status === 'fulfilled') setWarehouses(warehousesRes.value.data);
    } catch (err) { toast.error('Failed to load data'); }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (selectedVendor) {
      api.get(`/purchase-entries/vendor-pos/${selectedVendor}`).then(res => setVendorPOs(res.data)).catch(() => setVendorPOs([]));
    } else { setVendorPOs([]); }
  }, [selectedVendor]);

  const vendorMap = Object.fromEntries(vendors.map(v => [v.id, v]));
  const brandMap = Object.fromEntries(brands.map(b => [b.id, b.name]));
  const warehouseMap = Object.fromEntries(warehouses.map(w => [w.id, w.name]));

  const getVendorDisplay = (id) => {
    const v = vendorMap[id];
    if (!v) return id;
    const name = v.name || v.company_name || id;
    const loc = v.city?.toLowerCase() === 'mumbai' ? v.area : v.city;
    return loc ? `${name} (${loc})` : name;
  };

  const peSortOptions = [
    { value: 'date_desc', label: 'Newest First' },
    { value: 'date_asc', label: 'Oldest First' },
    { value: 'name_asc', label: 'A to Z (Vendor)' },
    { value: 'name_desc', label: 'Z to A (Vendor)' },
    { value: 'qty_desc', label: 'Qty: High to Low' },
    { value: 'qty_asc', label: 'Qty: Low to High' },
    { value: 'amount_desc', label: 'Cost: High to Low' },
    { value: 'amount_asc', label: 'Cost: Low to High' },
  ];

  // Filtering + Sorting
  const filteredEntries = useMemo(() => {
    let list = entries.filter(e => {
      if (searchGRN && !(vendorMap[e.vendor_id]?.name?.toLowerCase().includes(searchGRN.toLowerCase())) &&
          !(/\d/.test(searchGRN) && (e.grn_number?.toLowerCase().includes(searchGRN.toLowerCase()) ||
          e.invoice_number?.toLowerCase().includes(searchGRN.toLowerCase()))) &&
          !(e.items || []).some(item => item.design_code?.toLowerCase().includes(searchGRN.toLowerCase()))) return false;
      if (filterVendor && e.vendor_id !== filterVendor) return false;
      if (filterStates.length && !filterStates.includes(vendorMap[e.vendor_id]?.state)) return false;
      if (filterCities.length && !filterCities.includes(vendorMap[e.vendor_id]?.city)) return false;
      if (filterDateFrom && e.entry_date < filterDateFrom) return false;
      if (filterDateTo && e.entry_date > filterDateTo) return false;
      if (filterStaff.length && !filterStaff.includes(e.created_by_name)) return false;
      return true;
    });
    list.sort((a, b) => {
      const vA = vendorMap[a.vendor_id]?.name || '', vB = vendorMap[b.vendor_id]?.name || '';
      switch (sortBy) {
        case 'date_asc': return new Date(a.entry_date || a.created_at) - new Date(b.entry_date || b.created_at);
        case 'name_asc': return vA.localeCompare(vB);
        case 'name_desc': return vB.localeCompare(vA);
        case 'qty_desc': return (b.total_qty || 0) - (a.total_qty || 0);
        case 'qty_asc': return (a.total_qty || 0) - (b.total_qty || 0);
        case 'amount_desc': return (b.total_cost || 0) - (a.total_cost || 0);
        case 'amount_asc': return (a.total_cost || 0) - (b.total_cost || 0);
        default: return new Date(b.entry_date || b.created_at) - new Date(a.entry_date || a.created_at);
      }
    });
    return list;
  }, [entries, searchGRN, filterVendor, filterStates, filterCities, filterDateFrom, filterDateTo, filterStaff, sortBy]);

  const resetForm = () => {
    setSelectedVendor(''); setSelectedWarehouse(''); setEntryDate(new Date().toISOString().split('T')[0]);
    setInvoiceNumber(''); setChallanNumber(''); setEntryNotes('');
    setEntryItems([]); setSelectedPO(null); setSelectedPOItem(null);
    setManualDesignCode(''); setAddMode(null); setEditingEntry(null);
    setDuplicateInvoiceWarning(null);
  };

  const openCreateForm = () => {
    resetForm();
    setIsFormOpen(true);
  };

  const openEditForm = (entry) => {
    setEditingEntry(entry);
    setSelectedVendor(entry.vendor_id);
    setSelectedWarehouse(entry.warehouse_id || '');
    setEntryDate(entry.entry_date || '');
    setInvoiceNumber(entry.invoice_number || '');
    setChallanNumber(entry.challan_number || '');
    setEntryNotes(entry.notes || '');
    setEntryItems((entry.items || []).map(item => ({
      id: item.id || crypto.randomUUID(),
      product_id: item.product_id,
      design_code: item.design_code,
      linked_po_id: item.linked_po_id,
      linked_po_item_id: item.linked_po_item_id,
      po_number: item.po_number || null,
      is_delayed: false,
      variants: (item.variants || []).map(v => ({ ...v })),
    })));
    setIsFormOpen(true);
  };

  // Add item from PO
  const addPOLinkedItem = () => {
    if (!selectedPO || !selectedPOItem) { toast.error('Select a PO and item'); return; }
    const po = vendorPOs.find(p => p.id === selectedPO);
    const poItem = po?.items?.find(i => i.id === selectedPOItem);
    if (!poItem) return;
    const variants = poItem.variants.map(v => ({
      size: v.size, color: v.color,
      qty_received: Math.max(0, v.qty_ordered - (v.qty_received || 0)),
      cost: v.actual_cost || v.tentative_cost || 0,
      wsp_local: 0, wsp_all: 0, quality_status: 'ok',
    })).filter(v => v.qty_received > 0);
    if (variants.length === 0) { toast.error('No pending quantities for this item'); return; }
    setEntryItems(prev => [...prev, {
      id: crypto.randomUUID(), product_id: poItem.product_id, design_code: poItem.design_code,
      linked_po_id: selectedPO, linked_po_item_id: selectedPOItem, po_number: po.po_number,
      is_delayed: poItem.is_delayed, variants,
    }]);
    setSelectedPOItem(null);
    toast.success(`Added ${poItem.design_code} from ${po.po_number} — add more or close`);
  };

  const addManualItem = () => {
    if (!manualDesignCode.trim()) { toast.error('Enter design code'); return; }
    const product = products.find(p => p.design_code.toLowerCase() === manualDesignCode.trim().toLowerCase());
    setEntryItems(prev => [...prev, {
      id: crypto.randomUUID(), product_id: product?.id || null,
      design_code: manualDesignCode.trim().toUpperCase(),
      linked_po_id: null, linked_po_item_id: null, po_number: null, is_delayed: false,
      variants: [{ size: '', color: '', qty_received: 0, cost: 0, wsp_local: 0, wsp_all: 0, quality_status: 'ok' }],
    }]);
    setManualDesignCode('');
    toast.success(`Added ${manualDesignCode.trim().toUpperCase()} — add more or close`);
  };

  const updateVariant = (itemIdx, varIdx, field, value) => {
    setEntryItems(prev => {
      const next = [...prev];
      const item = { ...next[itemIdx], variants: [...next[itemIdx].variants] };
      item.variants[varIdx] = { ...item.variants[varIdx], [field]: field.includes('qty') || field === 'cost' || field.startsWith('wsp') ? (parseFloat(value) || 0) : value };
      next[itemIdx] = item;
      return next;
    });
  };

  const addVariantRow = (itemIdx) => {
    setEntryItems(prev => {
      const next = [...prev];
      next[itemIdx] = { ...next[itemIdx], variants: [...next[itemIdx].variants, { size: '', color: '', qty_received: 0, cost: 0, wsp_local: 0, wsp_all: 0, quality_status: 'ok' }] };
      return next;
    });
  };

  const removeItem = (idx) => setEntryItems(prev => prev.filter((_, i) => i !== idx));

  const removeVariant = (itemIdx, varIdx) => {
    setEntryItems(prev => {
      const next = [...prev];
      next[itemIdx] = { ...next[itemIdx], variants: next[itemIdx].variants.filter((_, i) => i !== varIdx) };
      return next;
    });
  };

  const submitEntry = async () => {
    if (!selectedVendor) { toast.error('Select a vendor'); return; }
    if (!selectedWarehouse) { toast.error('Select a warehouse'); return; }
    if (entryItems.length === 0) { toast.error('Add at least one item'); return; }
    if (duplicateInvoiceWarning) { toast.error('Duplicate invoice number detected. Please resolve before submitting.'); return; }
    for (const item of entryItems) {
      for (const v of item.variants) {
        if (!v.size || !v.color) { toast.error(`Fill size and color for ${item.design_code}`); return; }
        if (v.qty_received <= 0) { toast.error(`Enter quantity for ${item.design_code} (${v.size}/${v.color})`); return; }
      }
    }
    try {
      const payload = {
        vendor_id: selectedVendor, entry_date: entryDate,
        invoice_number: invoiceNumber || null, challan_number: challanNumber || null,
        warehouse_id: selectedWarehouse,
        items: entryItems.map(item => ({
          id: item.id, product_id: item.product_id, design_code: item.design_code,
          linked_po_id: item.linked_po_id, linked_po_item_id: item.linked_po_item_id,
          variants: item.variants,
        })),
        notes: entryNotes || null,
      };
      if (editingEntry) {
        await api.put(`/purchase-entries/${editingEntry.id}`, payload);
        toast.success(`${editingEntry.grn_number} updated successfully!`);
      } else {
        const res = await api.post('/purchase-entries', payload);
        toast.success(`Purchase Entry ${res.data.grn_number} created! Stock updated.`);
      }
      resetForm(); setIsFormOpen(false); loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save entry'); }
  };

  const deleteEntry = async (entryId, grn) => {
    try {
      await api.delete(`/purchase-entries/${entryId}`);
      toast.success(`${grn} deleted. Stock reversed.`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const printLabels = async (entry) => {
    try {
      const res = await api.get(`/purchase-entries/${entry.id}/labels`);
      const labels = res.data;
      if (!labels.labels.length) { toast.info('No labels to print for this entry'); return; }
      const win = window.open('', '_blank');
      if (!win) { toast.error('Please allow popups'); return; }
      writePrintHtml(win, `<!DOCTYPE html><html><head><title>Labels - ${h(labels.grn_number)}</title>
<style>@page{margin:5mm}body{margin:0;font-family:monospace}.grid{display:grid;grid-template-columns:repeat(3,1fr);gap:4mm}.label{border:1px solid #ccc;padding:3mm;text-align:center;break-inside:avoid}.label .design{font-size:14px;font-weight:bold}.label .sku{font-size:8px;color:#666;word-break:break-all}.label .details{font-size:11px;margin:2mm 0}.label .brand{font-size:10px;color:#444}.label img{margin:2mm auto}@media print{.no-print{display:none}}</style></head><body>
<div class="no-print" style="padding:10px;text-align:center"><button onclick="window.print()" style="font-size:16px;padding:10px 30px;cursor:pointer">Print Labels</button> (${h(labels.total)} labels)</div>
<div class="grid">${labels.labels.map(l => `<div class="label"><div class="brand">${h(l.brand)}</div><div class="design">${h(l.design_code)}</div><div class="details">${h(l.size)} / ${h(l.color)}</div><img src="data:image/png;base64,${h(l.qr_base64)}" width="90" height="90" /><div class="sku">${h(l.sku_code)}</div></div>`).join('')}</div></body></html>`);
      toast.success(`Generated ${labels.total} QR labels for ${labels.grn_number}`);
    } catch (err) { toast.error('Failed to generate labels'); }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2,'0')}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getFullYear()).slice(-2)}`;
  };
  const totalEntryQty = entryItems.reduce((s, i) => s + i.variants.reduce((s2, v) => s2 + (v.qty_received || 0), 0), 0);
  const totalEntryCost = entryItems.reduce((s, i) => s + i.variants.reduce((s2, v) => s2 + (v.qty_received || 0) * (v.cost || 0), 0), 0);
  const stateFilterOptions = [...new Set(vendors.map(v => v.state).filter(Boolean))].sort().map(s => ({ value: s, label: s }));
  const cityFilterOptions = [...new Set(vendors.filter(v => !filterStates.length || filterStates.includes(v.state)).map(v => v.city).filter(Boolean))].sort().map(c => ({ value: c, label: c }));
  const staffFilterOptions = [...new Set(entries.map(e => e.created_by_name).filter(Boolean))].sort().map(n => ({ value: n, label: n }));
  const hasActiveFilters = !!(searchGRN || filterVendor || filterStates.length || filterCities.length || filterDateFrom || filterDateTo || filterStaff.length);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" /></div>;

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="purchase-entry-container">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Purchase Entry</h1>
          <p className="text-gray-600 mt-1">Record received stock, link to POs, manage goods receipt</p>
        </div>
      </div>

      {/* Filters */}
      <Card data-testid="pe-filters">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-600">Search GRN / Invoice</Label>
              <Input value={searchGRN} onChange={e => setSearchGRN(e.target.value)} placeholder="GRN, Invoice, Vendor or Design No..." className="h-9" data-testid="pe-filter-search" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-600">Sort By</Label>
              <SortDropdown value={sortBy} onChange={setSortBy} options={peSortOptions} testId="pe-sort" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-600">Vendor</Label>
              <select value={filterVendor} onChange={e => setFilterVendor(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="pe-filter-vendor">
                <option value="">All Vendors</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-600">State</Label>
              <MultiSelectFilter label="All States" options={stateFilterOptions} selected={filterStates} onChange={setFilterStates} testId="pe-filter-state" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-600">City</Label>
              <MultiSelectFilter label="All Cities" options={cityFilterOptions} selected={filterCities} onChange={setFilterCities} testId="pe-filter-city" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-600">Date From</Label>
              <Input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className="h-9" data-testid="pe-filter-date-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-600">Date To</Label>
              <Input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className="h-9" data-testid="pe-filter-date-to" />
            </div>
            <div className="space-y-1 min-w-[130px]">
              <Label className="text-xs font-semibold text-gray-600">Staff</Label>
              <MultiSelectFilter label="All Staff" options={staffFilterOptions} selected={filterStaff} onChange={setFilterStaff} testId="pe-filter-staff" />
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="h-9 text-xs text-gray-500" onClick={() => { setSearchGRN(''); setFilterVendor(''); setFilterStates([]); setFilterCities([]); setFilterDateFrom(''); setFilterDateTo(''); setFilterStaff([]); }} data-testid="pe-clear-filters">
                Clear Filters
              </Button>
            )}
          </div>
          {hasActiveFilters && <p className="text-xs text-gray-500 mt-2">Showing {filteredEntries.length} of {entries.length} entries</p>}
        </CardContent>
      </Card>

      <PaginationBar currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} pageSize={PAGE_SIZE} onPageChange={(p) => setCurrentPage(p)} testIdPrefix="pe-page" />

      {/* Create / Edit Dialog */}
      <Dialog open={isFormOpen} onOpenChange={v => { if (!v) resetForm(); setIsFormOpen(v); }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto z-50">
          <DialogHeader>
            <DialogTitle>{editingEntry ? `Edit ${editingEntry.grn_number}` : 'New Purchase Entry (Goods Receipt)'}</DialogTitle>
            <DialogDescription>{editingEntry ? 'Update entry details, items and quantities. Stock will be recalculated.' : 'Record received stock from vendor. Link to PO for automatic tracking.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Vendor *</Label>
                <select value={selectedVendor} onChange={e => { setSelectedVendor(e.target.value); if (!editingEntry) setEntryItems([]); }} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="pe-vendor-select">
                  <option value="">Choose Vendor</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Warehouse *</Label>
                <select value={selectedWarehouse} onChange={e => setSelectedWarehouse(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="pe-warehouse-select">
                  <option value="">Choose Warehouse</option>
                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Date *</Label>
                <Input type="date" value={entryDate} onChange={e => setEntryDate(e.target.value)} className="h-9" data-testid="pe-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Invoice No.</Label>
                <Input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} onBlur={e => checkDuplicateInvoice(e.target.value, selectedVendor)} placeholder="INV-001" className={`h-9 ${duplicateInvoiceWarning ? 'border-red-500 ring-1 ring-red-500' : ''}`} data-testid="pe-invoice" />
                {duplicateInvoiceWarning && <p className="text-[10px] text-red-600 font-medium mt-0.5" data-testid="pe-duplicate-invoice-warning">{duplicateInvoiceWarning}</p>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Challan No.</Label>
                <Input value={challanNumber} onChange={e => setChallanNumber(e.target.value)} placeholder="CH-001" className="h-9" data-testid="pe-challan" />
              </div>
            </div>

            {/* Add Items */}
            {selectedVendor && (
              <Card className="border-dashed border-2 border-gray-300">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Label className="font-semibold text-sm">Add Items</Label>
                    <Button size="sm" variant={addMode === 'po_link' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setAddMode(addMode === 'po_link' ? null : 'po_link')} data-testid="pe-add-from-po">
                      Link from PO {vendorPOs.length > 0 && <Badge className="ml-1 h-4 text-[10px]">{vendorPOs.length}</Badge>}
                    </Button>
                    <Button size="sm" variant={addMode === 'manual' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setAddMode(addMode === 'manual' ? null : 'manual')} data-testid="pe-add-manual">
                      Manual Entry
                    </Button>
                  </div>
                  {addMode === 'po_link' && (
                    <div className="space-y-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      {vendorPOs.length === 0 ? (
                        <p className="text-sm text-gray-500">No open POs for this vendor.</p>
                      ) : (
                        <>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Select PO (Oldest first - LIFO)</Label>
                              <select value={selectedPO || ''} onChange={e => { setSelectedPO(e.target.value); setSelectedPOItem(null); }} className="flex h-8 w-full rounded border border-input bg-white px-2 text-xs" data-testid="pe-select-po">
                                <option value="">Choose PO</option>
                                {vendorPOs.map(po => <option key={po.id} value={po.id}>{po.po_number} ({po.po_date})</option>)}
                              </select>
                            </div>
                            <div>
                              <Label className="text-xs">Select Item</Label>
                              <select value={selectedPOItem || ''} onChange={e => setSelectedPOItem(e.target.value)} className="flex h-8 w-full rounded border border-input bg-white px-2 text-xs" data-testid="pe-select-po-item">
                                <option value="">Choose Design</option>
                                {selectedPO && vendorPOs.find(p => p.id === selectedPO)?.items?.map(i => {
                                  const pending = i.variants.reduce((s, v) => s + Math.max(0, v.qty_ordered - (v.qty_received || 0)), 0);
                                  return pending > 0 ? <option key={i.id} value={i.id}>{i.design_code} — {pending} pcs pending {i.is_delayed ? '(DELAYED)' : ''}</option> : null;
                                })}
                              </select>
                            </div>
                          </div>
                          {selectedPOItem && (() => {
                            const po = vendorPOs.find(p => p.id === selectedPO);
                            const item = po?.items?.find(i => i.id === selectedPOItem);
                            return item?.is_delayed ? (
                              <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700" data-testid="pe-delay-alert">
                                <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                                Order is DELAYED past expected delivery date ({item.delivery_date})
                              </div>
                            ) : null;
                          })()}
                          <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700" onClick={addPOLinkedItem} disabled={!selectedPO || !selectedPOItem} data-testid="pe-confirm-po-link">Add to Entry</Button>
                        </>
                      )}
                    </div>
                  )}
                  {addMode === 'manual' && (
                    <div className="flex gap-2 items-end p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex-1">
                        <Label className="text-xs">Design Code</Label>
                        <Input value={manualDesignCode} onChange={e => setManualDesignCode(e.target.value)} placeholder="e.g. DGN-001" className="h-8 text-xs" data-testid="pe-manual-design" onKeyDown={e => { if (e.key === 'Enter') addManualItem(); }} />
                      </div>
                      <Button size="sm" className="h-8 text-xs bg-amber-600 hover:bg-amber-700" onClick={addManualItem} data-testid="pe-confirm-manual">Add</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Items Table */}
            {entryItems.length > 0 && (
              <div className="space-y-4">
                <Label className="font-semibold">Items ({entryItems.length}) — Total: {totalEntryQty} pcs, Cost: ₹{totalEntryCost.toLocaleString('en-IN')}</Label>
                {entryItems.map((item, itemIdx) => (
                  <Card key={item.id} className={`border-l-4 ${item.linked_po_id ? 'border-l-blue-500' : 'border-l-amber-500'}`} data-testid={`pe-item-${itemIdx}`}>
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm">{item.design_code}</span>
                          {item.po_number && <Badge variant="outline" className="text-[10px] text-blue-700 border-blue-300">{item.po_number}</Badge>}
                          {item.is_delayed && <Badge className="bg-red-100 text-red-700 text-[10px]">DELAYED</Badge>}
                          {!item.linked_po_id && <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">Manual</Badge>}
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => addVariantRow(itemIdx)}>+ Row</Button>
                          <Button size="sm" variant="ghost" className="h-6 text-xs text-red-600" onClick={() => removeItem(itemIdx)}>Remove</Button>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] text-gray-500">Quick fill:</span>
                          <Button size="sm" variant="outline" className="h-5 px-2 text-[10px]"
                            onClick={() => {
                              const firstQty = item.variants[0]?.qty_received;
                              if (firstQty) { item.variants.forEach((_, vi) => updateVariant(itemIdx, vi, 'qty_received', firstQty)); }
                            }}
                            data-testid={`pe-copy-qty-${itemIdx}`}>
                            Copy Qty to All
                          </Button>
                          <Button size="sm" variant="outline" className="h-5 px-2 text-[10px]"
                            onClick={() => {
                              const firstCost = item.variants[0]?.cost;
                              if (firstCost) { item.variants.forEach((_, vi) => updateVariant(itemIdx, vi, 'cost', firstCost)); }
                            }}
                            data-testid={`pe-copy-cost-${itemIdx}`}>
                            Copy Cost to All
                          </Button>
                        </div>
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-2 py-1 text-left">Size</th>
                              <th className="px-2 py-1 text-left">Color</th>
                              <th className="px-2 py-1 text-right">Qty</th>
                              <th className="px-2 py-1 text-right">Cost (₹)</th>
                              <th className="px-2 py-1 text-right">WSP Local</th>
                              <th className="px-2 py-1 text-right">WSP All</th>
                              <th className="px-2 py-1 text-center">Quality</th>
                              <th className="px-2 py-1"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.variants.map((v, vIdx) => (
                              <tr key={vIdx} className="border-t">
                                <td className="px-1 py-1">
                                  <select value={v.size} onChange={e => updateVariant(itemIdx, vIdx, 'size', e.target.value)} className="h-7 w-full rounded border text-xs px-1">
                                    <option value="">Size</option>
                                    {enums.sizes?.map(s => <option key={s} value={s}>{s}</option>)}
                                  </select>
                                </td>
                                <td className="px-1 py-1">
                                  <select value={v.color} onChange={e => updateVariant(itemIdx, vIdx, 'color', e.target.value)} className="h-7 w-full rounded border text-xs px-1">
                                    <option value="">Color</option>
                                    {enums.colors?.map(c => <option key={c} value={c}>{c}</option>)}
                                  </select>
                                </td>
                                <td className="px-1 py-1"><Input type="number" min="0" value={v.qty_received || ''} onChange={e => updateVariant(itemIdx, vIdx, 'qty_received', e.target.value)} className="h-7 w-16 text-xs text-right" data-testid={`pe-qty-${itemIdx}-${vIdx}`} /></td>
                                <td className="px-1 py-1"><Input type="number" min="0" step="0.01" value={v.cost || ''} onChange={e => updateVariant(itemIdx, vIdx, 'cost', e.target.value)} className="h-7 w-20 text-xs text-right" data-testid={`pe-cost-${itemIdx}-${vIdx}`} /></td>
                                <td className="px-1 py-1"><Input type="number" min="0" step="0.01" value={v.wsp_local || ''} onChange={e => updateVariant(itemIdx, vIdx, 'wsp_local', e.target.value)} className="h-7 w-20 text-xs text-right" /></td>
                                <td className="px-1 py-1"><Input type="number" min="0" step="0.01" value={v.wsp_all || ''} onChange={e => updateVariant(itemIdx, vIdx, 'wsp_all', e.target.value)} className="h-7 w-20 text-xs text-right" /></td>
                                <td className="px-1 py-1">
                                  <select value={v.quality_status} onChange={e => updateVariant(itemIdx, vIdx, 'quality_status', e.target.value)} className={`h-7 rounded border text-xs px-1 ${v.quality_status === 'ok' ? 'text-green-700' : v.quality_status === 'defective' ? 'text-red-700' : 'text-yellow-700'}`}>
                                    <option value="ok">OK</option>
                                    <option value="defective">Defective</option>
                                    <option value="shortfall">Shortfall</option>
                                  </select>
                                </td>
                                <td className="px-1 py-1">
                                  {item.variants.length > 1 && <button onClick={() => removeVariant(itemIdx, vIdx)} className="text-red-400 hover:text-red-600 text-xs">✕</button>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Notes + Submit */}
            {entryItems.length > 0 && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Notes</Label>
                  <Textarea value={entryNotes} onChange={e => setEntryNotes(e.target.value)} placeholder="Goods receipt notes..." className="h-16" data-testid="pe-notes" />
                </div>
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => { resetForm(); setIsFormOpen(false); }}>Cancel</Button>
                  <Button onClick={submitEntry} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pe-submit">
                    {editingEntry ? 'Update Entry' : `Submit Entry`} ({totalEntryQty} pcs — ₹{totalEntryCost.toLocaleString('en-IN')})
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Entries List */}
      <Card data-testid="pe-list">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <CardTitle>Goods Receipt Notes ({filteredEntries.length})</CardTitle>
            {canCreate && (
              <Button size="sm" onClick={() => navigate('/create-purchase-entry')} className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-sm" data-testid="create-pe-button">
                + New Purchase Entry
              </Button>
            )}
          </div>
          <CardDescription>Purchase entries with stock receipt tracking</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredEntries.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{hasActiveFilters ? 'No matching entries' : 'No Purchase Entries Yet'}</h3>
              <p className="text-gray-600 mb-4">{hasActiveFilters ? 'Try adjusting your filters.' : 'Record your first goods receipt by creating a purchase entry.'}</p>
              {!hasActiveFilters && canCreate && <Button onClick={() => navigate('/create-purchase-entry')} className="bg-emerald-600">Create First Entry</Button>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pe-table">
                <thead>
                  <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wider">
                    <th className="px-4 py-3 text-left font-medium">GRN #</th>
                    <th className="px-4 py-3 text-left font-medium">Date</th>
                    <th className="px-2 py-3 w-10"><input type="checkbox" className="w-4 h-4 rounded" onChange={e => { if (e.target.checked) bulk.selectAll(filteredEntries.map(x => x.id)); else bulk.clearSelection(); }} /></th>
                    <th className="px-4 py-3 text-left font-medium">Vendor</th>
                    <th className="px-4 py-3 text-center font-medium">Items</th>
                    <th className="px-4 py-3 text-right font-medium">Qty</th>
                    <th className="px-4 py-3 text-right font-medium">Cost</th>
                    <th className="px-4 py-3 text-left font-medium">Warehouse</th>
                    <th className="px-4 py-3 text-left font-medium">Invoice / Challan</th>
                    <th className="px-4 py-3 text-left font-medium">By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredEntries.map(entry => (
                    <tr key={entry.id} className={`hover:bg-emerald-50/50 cursor-pointer transition-colors group ${bulk.isSelected(entry.id) ? "bg-blue-50" : ""}`} data-testid={`pe-row-${entry.id}`} onClick={() => navigate(`/purchase-entry/${entry.id}`)}>
                    <td className="px-2 py-3" onClick={e => e.stopPropagation()}><input type="checkbox" className="w-4 h-4 rounded" checked={bulk.isSelected(entry.id)} onChange={() => bulk.toggleSelect(entry.id)} /></td>
                      <td className="px-4 py-3">
                        <span className="font-bold text-emerald-700 font-mono group-hover:underline" data-testid={`pe-grn-link-${entry.id}`}>{entry.grn_number}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {formatDate(entry.entry_date)}
                        {entry.created_at && <span className="block text-[10px] text-gray-400">{new Date(entry.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900 max-w-[200px] truncate">{getVendorDisplay(entry.vendor_id)}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{entry.items?.length || 0}</td>
                      <td className="px-4 py-3 text-right font-medium">{entry.total_qty}</td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">₹{(entry.total_cost || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{warehouseMap[entry.warehouse_id] || '-'}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {entry.invoice_number && <span>INV: {entry.invoice_number}</span>}
                        {entry.invoice_number && entry.challan_number && <span> / </span>}
                        {entry.challan_number && <span>CH: {entry.challan_number}</span>}
                        {!entry.invoice_number && !entry.challan_number && <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 truncate max-w-[100px]">{entry.created_by_name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <BulkActionBar selectedCount={bulk.selectedCount} selectedIds={bulk.selectedIds} docType="PE" label="purchase entries" onComplete={() => { bulk.clearSelection(); fetchEntries(); }} onClear={bulk.clearSelection} />
    </div>
  );
};

export default PurchaseEntryManagement;
